import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const FEATURES = [
  "Scansione AI di scontrini e DDT",
  "Alert scadenze, stock basso, prodotti fermi",
  "Riordino fornitori su WhatsApp",
  "Multi-cassiere con permessi",
  "Export CSV/Excel di prodotti e vendite",
  "Riepilogo giornaliero via email",
];

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return null;
  }
}

function pickCopy(status, trialEndsAt) {
  // Ritorna { title, body } in base allo stato dell'abbonamento
  const trialEnd = formatDate(trialEndsAt);
  if (status === "trialing") {
    return {
      title: "Il periodo di prova è terminato",
      body: trialEnd
        ? `La prova gratuita del tuo negozio si è conclusa il ${trialEnd}. I tuoi dati sono al sicuro: riattivi tutto in un attimo con l'abbonamento.`
        : "La prova gratuita del tuo negozio si è conclusa. I tuoi dati sono al sicuro: riattivi tutto in un attimo con l'abbonamento.",
    };
  }
  if (status === "past_due") {
    return {
      title: "C'è un problema con il pagamento",
      body: "Il rinnovo dell'abbonamento non è andato a buon fine. I tuoi dati sono al sicuro. Aggiorna il metodo di pagamento per continuare.",
    };
  }
  if (status === "canceled") {
    return {
      title: "Abbonamento non attivo",
      body: "L'abbonamento del negozio è stato annullato. I tuoi dati sono conservati: riattivi tutto quando vuoi.",
    };
  }
  return {
    title: "Attiva l'abbonamento",
    body: "Per continuare a usare InPalmo con il tuo negozio, attiva l'abbonamento. I tuoi dati sono al sicuro.",
  };
}

export default function Subscription() {
  const { team, logout, refreshTeam } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const store = team?.store || null;
  const status = store?.subscription_status || "none";
  const trialEndsAt = store?.trial_ends_at || null;
  const isOwner = team?.role === "owner";
  const isGuest = team?.is_guest === true;

  const cancelled = params.get("checkout") === "cancel";
  useEffect(() => {
    // Pulizia query se presente (una volta sola al mount)
    if (params.get("checkout")) {
      const next = new URLSearchParams(params);
      next.delete("checkout");
      next.delete("session_id");
      setParams(next, { replace: true });
    }
  }, []);

  const { title, body } = pickCopy(status, trialEndsAt);

  const handleActivate = async () => {
    if (!store?.id || busy) return;
    setBusy(true);
    try {
      const res = await api.createCheckoutSession(store.id);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("URL della sessione mancante");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore nell'avvio del pagamento";
      toast.error("Impossibile avviare il pagamento", { description: String(msg) });
    } finally {
      // Sempre ripristinato: pulsante mai bloccato in stato "Attivazione..."
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch { /* noop */ }
    navigate("/entra", { replace: true });
  };

  const goDemo = async () => {
    try { localStorage.setItem("mag_guest", "1"); } catch { /* noop */ }
    // Forziamo un refresh per far ripartire il flusso in modalità guest
    try { await refreshTeam(); } catch { /* noop */ }
    navigate("/app/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="subscription-page">
      {/* Header con brand */}
      <header className="border-b border-[#E5E3DB] bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="subscription-brand-link">
            <div className="w-8 h-8 rounded-lg bg-[#2E4A3B] flex items-center justify-center">
              <Sparkles size={16} className="text-[#A8D4B8]" />
            </div>
            <span
              className="text-xl font-medium tracking-tight text-[#1A1C19]"
              style={{ fontFamily: "Work Sans, sans-serif" }}
            >
              In<span className="text-[#2E4A3B]">Palmo</span>
            </span>
          </Link>
          {!isGuest && (
            <button
              onClick={handleLogout}
              className="text-sm text-[#4A524C] hover:text-[#1A1C19] underline underline-offset-4"
              data-testid="subscription-logout-btn"
            >
              Esci
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-16">
        <div className="w-full max-w-2xl">
          {cancelled && (
            <div
              className="mb-6 rounded-xl border border-[#E5E3DB] bg-white px-4 py-3 text-sm text-[#4A524C] flex items-start gap-3"
              data-testid="subscription-cancel-notice"
            >
              <ArrowRight size={16} className="mt-0.5 text-[#8B6518] shrink-0" />
              <span>Nessun problema, il pagamento è stato annullato. Puoi riprovare quando vuoi.</span>
            </div>
          )}

          <div className="bg-white rounded-3xl border border-[#E5E3DB] shadow-sm p-8 sm:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#8B6518]">
              <ShieldCheck size={14} />
              <span>Il tuo negozio</span>
            </div>
            <h1
              className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-[#1A1C19]"
              style={{ fontFamily: "Work Sans, sans-serif" }}
              data-testid="subscription-title"
            >
              {title}
            </h1>
            <p className="mt-4 text-base text-[#4A524C] leading-relaxed" data-testid="subscription-body">
              {body}
            </p>

            {/* Prezzo + feature */}
            <div className="mt-8 rounded-2xl bg-[#F4F1ED] border border-[#E5E3DB] p-6">
              <div className="flex items-baseline gap-1">
                <span
                  className="text-4xl font-semibold tracking-tight text-[#2E4A3B]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                >
                  €19
                </span>
                <span className="text-sm text-[#4A524C]">/mese</span>
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-2">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#1A1C19]">
                    <CheckCircle2 size={16} className="text-[#2E4A3B] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA area */}
            <div className="mt-8">
              {isOwner ? (
                <Button
                  onClick={handleActivate}
                  disabled={busy}
                  size="lg"
                  className="w-full bg-[#2E4A3B] hover:bg-[#243A2E] text-white rounded-xl h-12 text-base font-medium"
                  data-testid="subscription-activate-btn"
                >
                  {busy ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Avvio pagamento…
                    </>
                  ) : (
                    <>
                      Attiva l&apos;abbonamento
                      <ArrowRight size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <div
                  className="rounded-xl border border-[#E5E3DB] bg-[#F4F1ED] p-4 text-sm text-[#1A1C19]"
                  data-testid="subscription-non-owner-notice"
                >
                  Chiedi al titolare del negozio di attivare l&apos;abbonamento. Non appena sarà attivo, potrai
                  rientrare nell&apos;app con il tuo accesso.
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                <button
                  onClick={goDemo}
                  className="text-[#4A524C] hover:text-[#1A1C19] underline underline-offset-4"
                  data-testid="subscription-demo-btn"
                >
                  Torna alla demo
                </button>
                {!isGuest && (
                  <button
                    onClick={handleLogout}
                    className="text-[#4A524C] hover:text-[#1A1C19] underline underline-offset-4"
                    data-testid="subscription-logout-inline-btn"
                  >
                    Esci dall&apos;account
                  </button>
                )}
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-[#4A524C]">
            I tuoi dati sono al sicuro. Nessuna cancellazione automatica. Cancellabile in ogni momento.
          </p>
        </div>
      </main>
    </div>
  );
}
